import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  SyntheticEvent,
} from 'react'
import React, { useEffect, useMemo, useState } from 'react'

import classnames from 'classnames'

import { AnimationWrapper } from '../AnimationWrapper'
import { ChevronEndLeftIcon } from '../Icons/Navigational/ChevronEndLeft'
import { ChevronEndRightIcon } from '../Icons/Navigational/ChevronEndRight'
import { ChevronLeftIcon } from '../Icons/Navigational/ChevronLeft'
import { ChevronRightIcon } from '../Icons/Navigational/ChevronRight'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import { SquareButtonLink } from '../SquareButtonLink'
import styles from './Pagination.module.scss'
import { getPageNumbersArray } from './Pagination.utils'

export const Pagination: FunctionComponent<Props> = ({
  className,
  totalPages = 1,
  currentPage = 1,
  callback = (input: number) => input,
  maxDisplayRange = 9,
  href,
  ...rest
}) => {
  const [selectedPage, setSelectedPage] = useState(currentPage)

  useEffect(() => {
    setSelectedPage(currentPage)
  }, [currentPage])

  const atStart = selectedPage === 1
  const atEnd = selectedPage === totalPages

  const handleClick = (pageNumber: number) => {
    if (pageNumber !== selectedPage) {
      callback(pageNumber)
      setSelectedPage(pageNumber)
    }
  }

  const pageNumbers = useMemo(
    () => getPageNumbersArray(totalPages, selectedPage, maxDisplayRange),
    [totalPages, selectedPage, maxDisplayRange]
  )

  if (totalPages < 2) {
    return null
  }

  return (
    <nav
      aria-label="pagination"
      className={classnames(styles.pagination, className)}
      data-testid="pagination"
      {...rest}
    >
      {!atStart && (
        <div data-testid="beginning-previous-button">
          <SquareButtonLink
            hasBorder
            onClick={(e: SyntheticEvent) => {
              e.preventDefault()
              handleClick(1)
            }}
            testID="beginning-button"
            data-which-id="pagination-first"
            className={styles.iconButtonMargin}
            href={href && href}
            aria-label="go to first page"
          >
            <ChevronEndLeftIcon />
          </SquareButtonLink>
          <SquareButtonLink
            hasBorder
            onClick={(e: SyntheticEvent) => {
              e.preventDefault()
              handleClick(selectedPage - 1)
            }}
            testID="previous-button"
            data-which-id="pagination-previous"
            className={styles.iconButtonMarginAlternate}
            aria-label="go to previous page"
            href={
              href &&
              (selectedPage === 2 ? href : `${href}?page=${selectedPage - 1}`)
            }
          >
            <ChevronLeftIcon />
          </SquareButtonLink>
        </div>
      )}

      <ul
        className={classnames(styles.numberBlock, styles.hideAtMobile, {
          [styles.isAtStart]: atStart,
          [styles.isAtEnd]: atEnd,
        })}
      >
        {pageNumbers.map((number) => {
          const { customProps, children } = getSquareButtonLinkProps(
            number,
            selectedPage
          )
          return (
            <li key={number}>
              <SquareButtonLink
                onClick={(e: SyntheticEvent) => {
                  e.preventDefault()
                  handleClick(number)
                }}
                data-which-id={`pagination-${number}`}
                isSelected={selectedPage === number}
                key={`page ${number}`}
                href={href && (number === 1 ? href : `${href}?page=${number}`)}
                {...customProps}
                className={styles.paginationNumber}
              >
                {children}
              </SquareButtonLink>
            </li>
          )
        })}
      </ul>
      <TypographyV2
        textStyle="sb-text-interface-body-small-regular"
        tag="p"
        className={classnames(styles.pageDisplay, styles.showAtMobile)}
        data-testid="page-display"
      >
        Page <span className={styles.selectedPage}>{selectedPage}</span> of{' '}
        {totalPages}
      </TypographyV2>

      {!atEnd && (
        <div data-testid="end-next-button">
          <SquareButtonLink
            hasBorder
            onClick={(e: SyntheticEvent) => {
              e.preventDefault()
              handleClick(selectedPage + 1)
            }}
            testID="next-button"
            data-which-id="pagination-next"
            className={styles.iconButtonMargin}
            aria-label="go to next page"
            href={href && `${href}?page=${selectedPage + 1}`}
          >
            <ChevronRightIcon />
          </SquareButtonLink>
          <SquareButtonLink
            hasBorder
            onClick={(e: SyntheticEvent) => {
              e.preventDefault()
              handleClick(totalPages)
            }}
            testID="end-button"
            data-which-id="pagination-last"
            aria-label="go to last page"
            href={href && `${href}?page=${totalPages}`}
          >
            <ChevronEndRightIcon />
          </SquareButtonLink>
        </div>
      )}
    </nav>
  )
}

///////// IMPLEMENTATION /////////

const getSquareButtonLinkProps = (number: number, selectedPage: number) => ({
  customProps: {
    'aria-current': number === selectedPage ? true : false,
    'aria-label':
      number === selectedPage
        ? `current page, page ${number}`
        : `go to page ${number}`,
  },
  children:
    number === selectedPage ? (
      number
    ) : (
      <span className="sb-link-primary">
        <AnimationWrapper>{number}</AnimationWrapper>
      </span>
    ),
})

type Props = {
  /**
   * Optional className
   */
  className?: string
  /**
   * Optional number for total number of pages
   */
  totalPages?: number
  /**
   * Optional number to set initial value for current page
   */
  currentPage?: number
  /**
   * Optional callback function
   */
  callback?: (pageNumber: number) => unknown
  /**
   * Optional number to control how many numbers buttons are rendered.
   */
  maxDisplayRange?: 9 | 11 | 13 | 15 | 17
  /**
   * Optional href for pagination links
   */
  href?: string
} & ComponentPropsWithoutRef<'nav'>
